<template>
    <span>
        <span @click="start">
            <slot :processing="processing" />
        </span>

        <Dialog :open="confirming" @update:open="closeModal">
            <DialogContent class="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{{ title }}</DialogTitle>
                </DialogHeader>

                <template v-if="offerPasskey && !codeSent">
                    <p class="text-sm text-muted-foreground">
                        Use your passkey, or we can email you a 6-digit code.
                    </p>

                    <div class="space-y-3">
                        <Button class="w-full" :disabled="passkeyLoading" @click="verifyPasskey">
                            <KeyRound class="size-4" />
                            Confirm with passkey
                        </Button>
                        <p v-if="passkeyError" class="text-xs text-destructive">{{ passkeyError }}</p>
                        <Button variant="outline" class="w-full" :disabled="processing" @click="sendCode">
                            Email me a code
                        </Button>
                    </div>
                </template>

                <p v-else class="text-sm text-muted-foreground">
                    {{ content }}
                </p>

                <div v-if="codeSent" class="flex justify-center py-4">
                    <OtpInput
                        input-classes="mt-2 rounded-md text-center border border-new-neutral-300 mx-1 sm:mx-2 focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:outline-hidden transition duration-200 ease-in-out"
                        separator=""
                        :num-inputs="6"
                        :should-auto-focus="true"
                        :is-input-num="true"
                        :error="error"
                        @on-complete="onComplete"
                    />
                </div>

                <DialogFooter>
                    <Button size="lg" variant="ghost" @click="closeModal">Cancel</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </span>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import OtpInput from '@/Pages/Auth/components/OtpInput.vue';
import { KeyRound } from 'lucide-vue-next';
import { usePasskeyVerification } from '@/composables/usePasskeyVerification';

const props = withDefaults(defineProps<{
    title?: string;
    content?: string;
    /**
     * Offer confirming with a passkey before emailing a code. A passkey only satisfies
     * `password.confirm` routes, not the OTP-only ones (email change, password set).
     */
    allowPasskey?: boolean;
}>(), {
    title: 'Verify your identity',
    content: 'We sent a 6-digit code to your email address. Please enter it below to continue.',
    allowPasskey: false,
});

const emit = defineEmits<{
    confirmed: [];
}>();

const confirming = ref(false);
const processing = ref(false);
const error = ref('');
const codeSent = ref(false);

const {
    verify: verifyPasskey,
    isLoading: passkeyLoading,
    isSupported: passkeysSupported,
    errorMessage: passkeyError,
} = usePasskeyVerification({
    routes: {
        options: route('passkey.confirm-options'),
        submit: route('passkey.confirm'),
    },
    onSuccess: () => {
        closeModal();
        nextTick(() => emit('confirmed'));
    },
});

const offerPasskey = computed(() => props.allowPasskey && passkeysSupported.value);

const start = () => {
    if (processing.value || confirming.value) return;

    if (!offerPasskey.value) {
        sendCode();
        return;
    }

    // Skip the prompt entirely while a previous confirmation is still fresh
    axios.get(route('password.confirmation')).then((response) => {
        if (response.data.confirmed) {
            emit('confirmed');
        } else {
            confirming.value = true;
        }
    });
};

const sendCode = () => {
    processing.value = true;
    error.value = '';

    axios
        .post(route('user.otp.store'))
        .then(() => {
            processing.value = false;
            codeSent.value = true;
            confirming.value = true;
        })
        .catch((err) => {
            processing.value = false;
            toast.error(err?.response?.data?.message ?? 'Failed to send verification code. Please try again.');
        });
};

const onComplete = (code: string) => {
    processing.value = true;
    error.value = '';

    axios
        .put(route('user.otp.update'), { code })
        .then(() => {
            processing.value = false;
            closeModal();
            nextTick(() => emit('confirmed'));
        })
        .catch((err) => {
            processing.value = false;
            error.value = err?.response?.data?.errors?.code?.[0] ?? 'Invalid code. Please try again.';
        });
};

const closeModal = () => {
    confirming.value = false;
    codeSent.value = false;
    error.value = '';
};
</script>
